"use client";

import Link from "next/link";
import { ArrowRight, TrendingUp } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { MotionView, MotionFadeUp } from "@/components/ui/motion-wrapper";

const defaultHighlights = [
  {
    client: "TechVentures India",
    title: "Cloud Migration Reduced Costs by 40%",
    metric: "40%",
    metricLabel: "Cost Reduction",
    description:
      "Migrated legacy infrastructure to AWS, cutting monthly hosting costs while improving uptime to 99.99%.",
    tags: ["AWS", "DevOps", "Cloud"],
  },
  {
    client: "FinEdge",
    title: "Fintech App Serving 100K+ Users",
    metric: "100K+",
    metricLabel: "Active Users",
    description:
      "Built a full-stack fintech platform from mobile app to backend API, now processing thousands of daily transactions.",
    tags: ["React Native", "Node.js", "Fintech"],
  },
  {
    client: "DataFlow Inc",
    title: "AI Analytics Boosted Revenue 25%",
    metric: "25%",
    metricLabel: "Revenue Increase",
    description:
      "Developed a custom AI analytics platform that transformed raw data into actionable business intelligence.",
    tags: ["AI/ML", "Python", "Analytics"],
  },
];

export function CaseStudyHighlights({
  eyebrow = "Proven Results",
  heading = "Real Results for Real Businesses",
  description = "See how we've helped companies achieve measurable growth with technology.",
  ctaHref = "/resources/case-studies",
  ctaLabel = "View All Case Studies",
  highlights = defaultHighlights,
}: {
  eyebrow?: string;
  heading?: string;
  description?: string;
  ctaHref?: string;
  ctaLabel?: string;
  highlights?: Array<{
    client: string;
    title: string;
    metric: string;
    metricLabel: string;
    description: string;
    tags: string[];
  }>;
}) {
  return (
    <section className="py-14 lg:py-28 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <MotionView className="text-center max-w-3xl mx-auto mb-16">
          <MotionFadeUp>
            <span className="text-sm font-semibold text-primary uppercase tracking-wider">
              {eyebrow}
            </span>
          </MotionFadeUp>
          <MotionFadeUp>
            <h2 className="mt-3 text-3xl lg:text-5xl font-bold text-foreground">
              {heading}
            </h2>
          </MotionFadeUp>
          <MotionFadeUp>
            <p className="mt-4 text-lg text-muted-foreground">
              {description}
            </p>
          </MotionFadeUp>
        </MotionView>

        <MotionView className="grid md:grid-cols-3 gap-6">
          {highlights.map((item) => (
            <MotionFadeUp key={item.title}>
              <Card className="h-full border-border hover:shadow-xl transition-all duration-300 hover:-translate-y-1 group overflow-hidden">
                <CardContent className="p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <TrendingUp className="w-4 h-4 text-emerald-500" />
                    <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      {item.client}
                    </span>
                  </div>
                  <div className="mb-4">
                    <span className="text-4xl font-bold gradient-text">{item.metric}</span>
                    <span className="block text-sm text-muted-foreground mt-1">{item.metricLabel}</span>
                  </div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">{item.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                    {item.description}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {item.tags.map((tag) => (
                      <span
                        key={tag}
                        className="text-xs px-2.5 py-1 rounded-full bg-primary/10 text-primary font-medium"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </MotionFadeUp>
          ))}
        </MotionView>

        <MotionView className="text-center mt-10">
          <MotionFadeUp>
            <Link
              href={ctaHref}
              className="inline-flex items-center gap-2 text-primary font-semibold hover:underline"
            >
              {ctaLabel} <ArrowRight className="w-4 h-4" />
            </Link>
          </MotionFadeUp>
        </MotionView>
      </div>
    </section>
  );
}
